import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import classNames from "classnames"

import { useAppDispatch, useAppSelector } from "store/hooks"
import { setSelectedDevicesForPerformanceTest } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"
import PerformanceTestStatus from "types/enums/PerformanceTestStatus"

import { convertDataToPerformanceTestTableRows } from "utils/convertData"
import { PerformanceTestActionsModal } from "modals/PerformanceTestActionsModal"
import { PerformanceTestCancelModal } from "modals/PerformanceTestCancelModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import { ButtonNew } from "components/form/ButtonNew"
import TableWrapper from "components/table/TableWrapper"
import { WidgetPerformanceTest } from "components/widgets/WidgetPerformanceTest"

import styles from "./index.module.scss"

interface PerformanceTestContentProps {
  devices: ApiTypes.Model.Device[]
  meta: ApiTypes.Model.Pagination | undefined
  isFetching: boolean
  tableHeaderCells: UITypes.TableHeaderCell[]
  columnsLoaded: boolean
  onActionSuccess: () => void
  onExport: () => void
}

const PerformanceTestContent = ({
  devices,
  meta,
  isFetching,
  tableHeaderCells,
  columnsLoaded,
  onExport,
  onActionSuccess,
}: PerformanceTestContentProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [isHeaderChecked, setHeaderChecked] = useState(false)
  const [showPerformanceTestActionsModal, setShowPerformanceTestActionsModal] = useState(false)
  const [showPerformanceTestCancelModal, setShowPerformanceTestCancelModal] = useState(false)
  const { devices: selectedDevices } = useAppSelector(state => state.tempData.selectedDevicesForPerformanceTest)

  const allDevicesSelected = () => {
    if (devices.length === 0) return false
    return devices.every(item => selectedDevices.findIndex(device => device.id === item.id) > -1)
  }

  useEffect(() => {
    dispatch(setSelectedDevicesForPerformanceTest({ devices: [] }))
  }, [])

  useEffect(() => {
    setHeaderChecked(allDevicesSelected())
  }, [devices, selectedDevices])

  const handlePerformanceTestActions = () => {
    setShowPerformanceTestActionsModal(true)
  }

  const handleCancelPerformanceTest = () => {
    setShowPerformanceTestCancelModal(true)
  }

  const onSelectDeviceForPerformanceTest = (id?: number) => {
    if (selectedDevices?.some(item => item.id === id)) {
      dispatch(setSelectedDevicesForPerformanceTest({ devices: selectedDevices?.filter(item => item.id !== id) }))
    } else {
      const selectedItem = devices.find(item => item.id === id)

      if (!selectedItem) return

      dispatch(
        setSelectedDevicesForPerformanceTest({
          isImmediate: false,
          devices: [
            ...selectedDevices,
            ...[
              {
                id: selectedItem.id,
                email: selectedItem.email,
                sn: selectedItem.sn,
                system_type: selectedItem.system_type,
                status: selectedItem.perftest.status,
              },
            ],
          ],
        }),
      )
    }
  }

  const toggleAllDevices = () => {
    if (isHeaderChecked) {
      dispatch(
        setSelectedDevicesForPerformanceTest({
          devices: selectedDevices?.filter(item => devices.findIndex(device => device.id === item.id) === -1),
        }),
      )
      setHeaderChecked(false)
    } else {
      dispatch(
        setSelectedDevicesForPerformanceTest({
          devices: [
            ...selectedDevices,
            ...devices
              .filter(item => selectedDevices.findIndex(device => device.id === item.id) === -1)
              .map(item => ({
                id: item.id,
                email: item.email,
                sn: item.sn,
                system_type: item.system_type,
                status: item.perftest.status,
              })),
          ],
        }),
      )
      setHeaderChecked(true)
    }
  }

  const handleSuccess = () => {
    setShowPerformanceTestActionsModal(false)
    setShowPerformanceTestCancelModal(false)
    onActionSuccess()
  }

  return (
    <>
      <div className={styles.widgetsWrapper}>
        <WidgetPerformanceTest />
      </div>
      <ContentWrapper title="Performance Test">
        <nav className={styles.tableNavWrapper}>
          <div className={styles.tableNavButtonsWrapper}>
            <ButtonNew
              className={classNames(styles.tableNavItem, styles.startPerformanceButton)}
              type="submit"
              disabled={selectedDevices.length === 0}
              onClick={handlePerformanceTestActions}
            >
              Start Performance Test
            </ButtonNew>
            <ButtonNew
              className={styles.cancelButton}
              type="submit"
              disabled={selectedDevices.every(
                item => ![PerformanceTestStatus.RESCHEDULED, PerformanceTestStatus.SCHEDULED].includes(item.status),
              )}
              onClick={handleCancelPerformanceTest}
            >
              CANCEL
            </ButtonNew>
          </div>
          <div className={styles.tableNavButtonsWrapper}>
            <EditColumnsButton tableName="contractor_performance_test_list" tableHeaderCells={tableHeaderCells} />
            <ButtonNew className={styles.tableNavItem} onClick={() => navigate("/perf-test-history")}>
              History
            </ButtonNew>
            <ButtonNew className={styles.tableNavItem} onClick={onExport} disabled={meta?.total === 0}>
              Export
            </ButtonNew>
          </div>
        </nav>
        <TableWrapper
          headerCells={tableHeaderCells}
          columnsLoaded={columnsLoaded}
          data={convertDataToPerformanceTestTableRows(devices)}
          isLoading={isFetching}
          onCheckboxChange={onSelectDeviceForPerformanceTest}
          onHeaderCheckboxChange={toggleAllDevices}
          currentItemIDs={selectedDevices.map(item => item.id)}
          isHeaderChecked={isHeaderChecked}
        />
        <Pagination meta={meta} />
      </ContentWrapper>
      {showPerformanceTestActionsModal && (
        <PerformanceTestActionsModal
          onCancel={() => setShowPerformanceTestActionsModal(false)}
          onSuccess={handleSuccess}
        />
      )}
      {showPerformanceTestCancelModal && (
        <PerformanceTestCancelModal
          onCancel={() => setShowPerformanceTestCancelModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </>
  )
}

export default PerformanceTestContent
